import { useMemo } from 'react';
import { Collaborator } from '../types';

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export const useSplitValidation = (
  songTitle: string,
  collaborators: Collaborator[]
): ValidationResult => {
  return useMemo(() => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Song title validation
    if (!songTitle.trim()) {
      errors.push('Song title is required');
    }

    // Collaborators validation
    if (collaborators.length === 0) {
      errors.push('At least one collaborator is required');
    }

    collaborators.forEach((collaborator, index) => {
      if (!collaborator.name.trim()) {
        errors.push(`Collaborator ${index + 1}: Name is required`);
      }
      
      if (collaborator.share <= 0) {
        errors.push(`Collaborator ${index + 1}: Share must be greater than 0`);
      }
      
      if (collaborator.share > 100) {
        errors.push(`Collaborator ${index + 1}: Share cannot exceed 100%`);
      }
    });

    // Total share validation
    const totalShare = collaborators.reduce((sum, c) => sum + c.share, 0);
    if (totalShare !== 100) {
      if (totalShare > 100) {
        errors.push(`Total share exceeds 100% (currently ${totalShare}%)`);
      } else if (totalShare > 0) {
        warnings.push(`Total share is ${totalShare}%. Must equal 100%.`);
      }
    }

    // Check for duplicate names
    const names = collaborators.map(c => c.name.trim().toLowerCase()).filter(name => name);
    const duplicateNames = names.filter((name, index) => names.indexOf(name) !== index);
    if (duplicateNames.length > 0) {
      warnings.push('Some collaborators have the same name');
    }

    // Check for duplicate emails
    const emails = collaborators.map(c => c.email?.trim().toLowerCase()).filter(email => email);
    const duplicateEmails = emails.filter((email, index) => emails.indexOf(email) !== index);
    if (duplicateEmails.length > 0) {
      errors.push('Duplicate email addresses are not allowed. Each collaborator must have a unique email.');
    }

    return {
      isValid: errors.length === 0 && totalShare === 100,
      errors,
      warnings
    };
  }, [songTitle, collaborators]);
};
